import React, { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer } from "react";
import { Action, initialState, State } from "src/app/state/stateReducer";
import { fetchConfiguration, fetchTraductions } from "src/app/api/PublicApi";
import { fetchBai, fetchCybersafe, fetchFavoris, fetchMenuGauche, fetchServices } from "src/app/api/AccountApi";
import { fetchAuthorizedAffilies } from "src/app/api/AffilieApi";
import { fetchMenu } from "src/app/api/MenuApi";


const StateContext = createContext<[State, Dispatch<Action>]>([
  initialState,
  () => {
    /*intential empty*/
  },
]);

type StateProviderProps = {
  initialState: State;
  stateReducer: (prevState: State, action: Action) => State;
  children: ReactNode;
  uuidEmployeurOuTiersInUrl: string | undefined | null;
};

/**
 * Component in charge of injecting a context providing the application's
 * current state and a dispatch method responsible for state updates
 * @param props
 */
export const StateProvider = (props: StateProviderProps) => {
  // initialisation du [state, dispatch] qui sera injecté via le contexte
  const [state, dispatch] = useReducer(props.stateReducer, props.initialState);

  useEffect(() => {
    /*
    Les traductions sont chargées une premiere fois avant l'application
    Du coup on utilise deux state "langue" et "langueDemandee"
    */
    if (state.langue) {
      fetchTraductions(state.langue).then((result) => {
        dispatch({ type: "setTraductions", payload: result });
      });
    }
  }, [state.langue, dispatch]);


  useEffect(() => {

    fetchFavoris().then(res => {
      dispatch({ type: "setFavoris", payload: res });
    });

    fetchBai().then(res => {
      dispatch({ type: "setBai", payload: res });
    });

    fetchCybersafe().then(res => {
      dispatch({ type: "setCybersafe", payload: res });
    });

    if (state.user?.typePersonne !== "Externe" || state.user?.comptePersonnel) {
      fetchServices().then(res => {
        dispatch({ type: "setServices", payload: res });
      });

      fetchMenuGauche().then(res => {
        dispatch({ type: "setMenuGauche", payload: res });
      });

      fetchMenu().then(res => {
        dispatch({ type: "setMenu", payload: res });
      });

    } else if (state.user) {
      fetchAuthorizedAffilies().then(res => {
        dispatch({ type: "setEmployeurs", payload: res });
      })
      .catch(res => {
        dispatch({ type: "setEmployeurs", payload: [] });
      });
    }
  }, [state.user]);

  useEffect(() => {
    if (state.employeurs) {
      const employeurContextuel = props.uuidEmployeurOuTiersInUrl ?
        state.employeurs?.find(affilie => {
          if(affilie.uuidEmployeur) {
            return affilie.uuidEmployeur === props.uuidEmployeurOuTiersInUrl!;
          } else {
            return affilie.noTiers === props.uuidEmployeurOuTiersInUrl!
          }
        })
        : state.employeurs?.[0];

      dispatch({ type: "setEmployeurContextuel", payload: employeurContextuel! });
    }

  }, [state.employeurs]);

  useEffect(() => {
    if (state?.employeurContextuel?.uuidEmployeur || state?.employeurContextuel?.noTiers) {
      fetchServices(state?.employeurContextuel?.uuidEmployeur, state?.employeurContextuel?.noTiers).then(res => {
          dispatch({ type: "setServices", payload: res });
        }
      );

      fetchMenuGauche(state?.employeurContextuel?.uuidEmployeur, state?.employeurContextuel?.noTiers).then(res => {
          dispatch({ type: "setMenuGauche", payload: res });
        }
      );

      fetchMenu(state?.employeurContextuel?.uuidEmployeur, state?.employeurContextuel?.noTiers).then(res => {
          dispatch({ type: "setMenu", payload: res });
        }
      );

      fetchConfiguration(state?.employeurContextuel?.agence).then(res => {
          dispatch({ type: "setConfiguration", payload: res });
        }
      );

    }
  }, [state.employeurContextuel]);


  return (
    <StateContext.Provider value={[state, dispatch]}>
      {props.children}
    </StateContext.Provider>
  );
};

/**
 * Returns a dispatch method to trigger state updates if necessary
 */
export const useAppState = () => useContext(StateContext);
